/**
 * SUB-5.91 · F-99, first half. What a figure is computed over, carried with the
 * figure so it cannot be read as though it were computed over everything.
 *
 * ⚠️ THIS MODULE DECIDES WHAT A NUMBER MEANS. IT BOUNDS NOTHING.
 *
 * Same standing rule as `mergedMetrics.ts`: the accounts a merged surface may
 * span is `merged_scope_tenant_ids()` evaluated inside the policy. Nothing here
 * may ever be the reason a row is absent. Qualifying a figure changes a CAPTION,
 * never a record.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS, MEASURED 2026-08-18 AGAINST LIVE DATA
 *
 * `mergedMetrics.ts` classifies whether a quantity may be added up across
 * accounts. It has nothing to say about whether the answer means anything, and
 * `system_cost_total` is the case that separates the two. It is correctly
 * registered PORTABLE — a currency sum is semantically portable, and FR46 names
 * currency explicitly — and summing it would still have printed a wrong
 * headline:
 *
 * | Account | Projects | Cost entered | Coverage |
 * |---|---|---|---|
 * | Solerro | 2,427 | 381 | **15.7%** |
 * | Quality Solar | 1,020 | 90 | **8.8%** |
 * | Southeast Energy | 760 | 723 | 95.1% |
 * | Testing | 557 | 0 | **0%** |
 *
 * ⚠️ **`NULL` AND `0` ARE DIFFERENT ANSWERS AND THE DISTINCTION IS LOAD-BEARING.**
 * Counting non-null puts Solerro at 38.1%, not 15.7%, because 543 of its rows
 * store `0`. A $0 residential solar install does not exist; those rows are an
 * untouched field, not a free job. So a currency basis treats `0` as absent,
 * `zeroCounts: false`, and the whole 16% figure F-99 reported depends on it. A
 * count-based basis would want the opposite, which is why this is a property of
 * the basis rather than a constant.
 *
 * ---------------------------------------------------------------------------
 * THE DECISION THIS ENCODES (Allan, 2026-08-18, F-99)
 *
 * A figure with a coverage basis ALWAYS carries its denominator. Not a badge,
 * not a static readiness flag — the rows it is actually computed over, recomputed
 * every time it is shown.
 *
 * ⚠️ A static readiness axis was considered and REJECTED: coverage changes as
 * data is entered, so a classification goes stale while a computed denominator
 * cannot. Withholding the metric outright was also rejected, because Southeast
 * Energy records cost on 95% of its projects and would have been punished for
 * the accounts that do not.
 *
 * ⚠️ THE TYPE IS THE ENFORCEMENT. `QualifiedFigure` has no unwrapped numeric
 * member and `formatQualifiedFigure` is the only formatter, so a caller cannot
 * reach a bare total without deliberately destructuring past the point of this
 * module. A convention would have been forgotten by the second surface.
 */

/**
 * What has to be true of a row for it to contribute to a figure.
 *
 * `column` is named rather than inferred so the caption can be checked against
 * the query that produced it: a coverage claim computed over a different column
 * from the figure is worse than no coverage claim.
 */
export interface CoverageBasis {
  /** The column a row must have populated to contribute. */
  column: string;
  /** One contributing row, singular, as it appears in the caption. */
  rowNoun: string;
  /** Plural of `rowNoun`. English is irregular; do not derive it. */
  rowNounPlural: string;
  /**
   * ⚠️ Whether a stored `0` means "answered, and the answer is zero" or "nobody
   * filled this in". False for currency — see the header. True for anything
   * where zero is a real observation, such as a count of attachments.
   */
  zeroCounts: boolean;
}

/** What a figure was actually computed over. */
export interface CoverageStat {
  /** Rows that contributed. */
  contributing: number;
  /** Rows in scope, contributing or not. */
  total: number;
}

/**
 * A number that cannot be rendered without saying what it covers.
 *
 * `value` is nullable on purpose and the null is not an error state: when no row
 * contributes, the honest answer is that the quantity is unrecorded. See
 * `qualifiedFigure`.
 */
export interface QualifiedFigure {
  value: number | null;
  coverage: CoverageStat;
  basis: CoverageBasis;
}

export const CURRENCY_COVERAGE_BASIS: CoverageBasis = {
  column: 'system_cost',
  rowNoun: 'project',
  rowNounPlural: 'projects',
  // See the header: 543 live rows store 0 and none of them is a free install.
  zeroCounts: false,
};

/** Whether one stored value contributes, under this basis. */
export function valueContributes(
  value: number | null | undefined,
  basis: CoverageBasis,
): boolean {
  if (value === null || value === undefined) return false;
  if (!Number.isFinite(value)) return false;
  if (value === 0) return basis.zeroCounts;
  return true;
}

/** Coverage over a set of stored values. */
export function coverageOf(
  values: readonly (number | null | undefined)[],
  basis: CoverageBasis,
): CoverageStat {
  let contributing = 0;
  for (const value of values) if (valueContributes(value, basis)) contributing += 1;
  return { contributing, total: values.length };
}

/**
 * Coverage across accounts. The roll-up spans a set, so its denominator is the
 * set's denominator.
 *
 * ⚠️ Sums both sides rather than averaging the percentages. Averaging would give
 * Testing's 0% over 557 projects the same weight as Dealer Management's 100%
 * over 1, which is Simpson's paradox with a straight face.
 */
export function combineCoverage(stats: readonly CoverageStat[]): CoverageStat {
  return stats.reduce<CoverageStat>(
    (acc, stat) => ({
      contributing: acc.contributing + stat.contributing,
      total: acc.total + stat.total,
    }),
    { contributing: 0, total: 0 },
  );
}

/** Coverage as a whole percentage, or null when nothing is in scope. */
export function coveragePercent(stat: CoverageStat): number | null {
  if (stat.total <= 0) return null;
  return Math.round((stat.contributing / stat.total) * 100);
}

/**
 * Pair a computed value with its coverage.
 *
 * ⚠️ WHEN NOTHING CONTRIBUTES THE VALUE IS FORCED TO NULL, and this is a
 * correctness rule rather than tidiness. `sum()` over no rows is SQL NULL, but a
 * frontend that has already coalesced it renders `$0` — and `$0` is a claim that
 * the work was free, not a statement that nobody recorded a price. The Testing
 * account has 557 projects and 0 of them priced; it must not report `$0`.
 */
export function qualifiedFigure(
  value: number | null | undefined,
  coverage: CoverageStat,
  basis: CoverageBasis,
): QualifiedFigure {
  return {
    value: coverage.contributing > 0 && value !== null && value !== undefined ? value : null,
    coverage,
    basis,
  };
}

/**
 * ⚠️ LOAD-BEARING COPY, NOT CHROME. This caption is the only thing standing
 * between a 16%-covered sum and a reader who assumes it covers everything.
 * `metricCoverage.test.ts` asserts on these strings deliberately, so a tidy-up
 * of "just wording" fails a test.
 */
export const NO_ROWS_IN_SCOPE_CAPTION = 'No projects in scope';

export function coverageCaption(stat: CoverageStat, basis: CoverageBasis): string {
  if (stat.total <= 0) return NO_ROWS_IN_SCOPE_CAPTION;

  const pct = coveragePercent(stat);
  const noun = stat.total === 1 ? basis.rowNoun : basis.rowNounPlural;
  return `${stat.contributing.toLocaleString()} of ${stat.total.toLocaleString()} ${noun} (${pct}%)`;
}

/**
 * The figure and its caption, together, as the only way out of this module.
 *
 * `formatValue` is injected because currency formatting is a presentation
 * concern and this module stays free of locale and symbol decisions — the same
 * reason it lives in `lib/` with no React import.
 */
export function formatQualifiedFigure(
  figure: QualifiedFigure,
  formatValue: (value: number) => string,
): { value: string; caption: string } {
  const caption = coverageCaption(figure.coverage, figure.basis);

  if (figure.value === null) {
    return {
      // Not "$0", not "—". The distinction the caption then quantifies.
      value: 'Not recorded',
      caption,
    };
  }

  return { value: formatValue(figure.value), caption };
}
